import _ from 'lodash';
import { DateTime } from 'luxon';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import { isServiceChargeItem } from '~Utils/serviceCharge';
import { LocalItem } from '~Types';

const getItemCourseId = (item: LocalItem) => item.type ?? item.courseType?.id ?? null;
import { getBongData_stations } from './__queries__';
import { BongGroupOrderInfo } from './BongGroup';
import { StorageData } from '~Components/Storage';
import { Printer } from '~reducers';

interface KDSProcessingParams {
  items: LocalItem[];
  stations: getBongData_stations[];
  orderInfo: BongGroupOrderInfo;
  printers: Printer[];
  storage: StorageData;
  licenses: { hasKDS: boolean };
  onlyPhysicalBongs?: boolean;
  orderStatusApi: any;
  orderStatus: any;
  logger: any;
}

export interface KDSProcessingResult {
  orderId: string | undefined;
  orderIdsByCourse: { [courseId: string]: string };
  itemsToAdd: LocalItem[];
  homelessItems: LocalItem[];
}

export function processKDSOrder({
  items,
  stations,
  orderInfo,
  printers,
  storage,
  licenses,
  onlyPhysicalBongs,
  orderStatusApi,
  orderStatus,
  logger,
}: KDSProcessingParams): KDSProcessingResult {
  logger.info('[KDS] Processing KDS order', {
    items,
    stations,
    orderInfo,
    printers,
    storage,
    licenses,
    onlyPhysicalBongs,
  });

  // Get all station items and categories for KDS logic
  const allStationItems = _.flatMap(stations, (station) => station.items);
  const allStationCategories = _.flatMap(stations, (station) => station.categories);

  // Partition items based on if they belong to a station or not for KDS
  const printerStationsAreUsed =
    printers.some((printer) => printer.stations.length > 0) ||
    (licenses.hasKDS && stations.length > 0);
  const isAppOrder = !!orderInfo.realId;

  const [itemsToAdd, homelessItems] =
    !printerStationsAreUsed || (isAppOrder && !storage.enableNewKDSAppOrders)
      ? [items, []]
      : // Otherwise, filter for relevant items
        _.partition(items, (item) => {
          return (
            allStationItems.some((stItem) => stItem.id === item.itemId) ||
            allStationCategories.some(
              (stCat) => stCat.id === item.categoryId || stCat.name === item.categoryId,
            )
          );
        });

  logger.info('[KDS] Partitioned items', {
    itemsToAdd,
    homelessItems,
  });

  // These items won't be added to the KDS, logged for debug purposes
  if (homelessItems.length > 0 && isAppOrder) {
    logger.warn('Skipped items for app order ', {
      items: homelessItems,
      orderRealId: orderInfo.realId,
    });
  }

  // Store KDS orderId and pass it along to any eventual physical bong prints
  let orderId: string | undefined;
  const orderIdsByCourse: { [courseId: string]: string } = {};

  logger.info('[KDS] Adding items to KDS', {
    hasKDS: licenses.hasKDS,
    itemsToAdd,
    onlyPhysicalBongs,
  });

  // If we use KDS and have bongable items, add bong to KDS.
  if (licenses.hasKDS && itemsToAdd.length > 0 && !onlyPhysicalBongs) {
    logger.info("[KDS] We're using KDS and have bongable items, add bong to KDS");

    const nonServiceChargeItems = itemsToAdd.filter((item) => !isServiceChargeItem(item.title));

    if (storage.KDSCourseOrdersMode) {
      const groupedByCourse = _.groupBy(nonServiceChargeItems, (item) => getItemCourseId(item) || 'NO_COURSE');
      const sortedCourseKeys = Object.keys(groupedByCourse).sort((a, b) => {
        const aOrder = groupedByCourse[a][0]?.courseType?.order ?? Number.MAX_SAFE_INTEGER;
        const bOrder = groupedByCourse[b][0]?.courseType?.order ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });

      sortedCourseKeys.forEach((courseKey, index) => {
        const courseItems = groupedByCourse[courseKey];
        if (!courseItems || courseItems.length === 0) {
          return;
        }

        const courseId = courseKey === 'NO_COURSE' ? null : courseKey;

        const existingOrderForCourse = orderStatus.orders.find(
          (order: any) =>
            order.tableId &&
            order.tableId === orderInfo.tableId &&
            (order.courseId || null) === courseId &&
            order.status === ORDER_STATUS.ACCEPTED &&
            DateTime.now() < DateTime.fromISO(order.createdAt).plus({ seconds: 180 }),
        );

        const orderHasComment = orderInfo.comment && orderInfo.comment.length > 0;
        const shouldBeFired = storage.holdFirstCourse ? false : index === 0;
        let courseOrderId: string;

        if (existingOrderForCourse && !orderHasComment) {
          logger.info("[KDS] Order existing for course, don't create new order", {
            tableId: orderInfo.tableId,
            courseId,
          });
          courseOrderId = existingOrderForCourse.id;

          if (shouldBeFired && existingOrderForCourse.fired === false) {
            orderStatusApi.fireOrder(courseOrderId);
          }
        } else {
          logger.info('[KDS] No order existing for course, create new order', {
            tableId: orderInfo.tableId,
            courseId,
            fired: shouldBeFired,
          });
          courseOrderId = orderStatusApi.addOrder(
            orderInfo.realId || '',
            orderInfo.comment || '',
            ORDER_STATUS.ACCEPTED,
            orderInfo.serviceType,
            orderInfo.tableName || orderInfo.orderNumber,
            orderInfo.tableId,
            orderInfo.origin,
            orderInfo.desiredTime,
            courseId,
            shouldBeFired,
          );
        }

        if (!orderId) {
          orderId = courseOrderId;
        }

        orderIdsByCourse[courseKey] = courseOrderId;

        logger.info('[KDS] Add course items to KDS', {
          orderId: courseOrderId,
          courseId,
          itemCount: courseItems.length,
        });

        courseItems.forEach((item) => {
          logger.info('[KDS] Adding item to KDS');
          orderStatusApi.addItem(
            item.itemId,
            item.categoryId,
            courseOrderId,
            item.message || '',
            item.variations.map((v) => v.id),
            item.removedVariations.map((v) => v.id),
            (item.defaultOrderStatus as ORDER_STATUS) || ORDER_STATUS.ACCEPTED,
            item.id,
            item.parentId,
            getItemCourseId(item) || undefined,
          );
        });
      });
    } else {
      // See if there is an order for this table already, that is collecting orders
      const existingOrderForTable = orderStatus.orders.find(
        (order: any) =>
          order.tableId &&
          order.tableId === orderInfo.tableId &&
          order.status === ORDER_STATUS.ACCEPTED &&
          DateTime.now() < DateTime.fromISO(order.createdAt).plus({ seconds: 180 }),
      );

      const orderHasComment = orderInfo.comment && orderInfo.comment.length > 0;

      if (existingOrderForTable && !orderHasComment) {
        logger.info("[KDS] Order existing for table, don't create new order");
        orderId = existingOrderForTable.id;
      } else {
        logger.info('[KDS] No order existing for table, create new order');
        orderId = orderStatusApi.addOrder(
          orderInfo.realId || '',
          orderInfo.comment || '',
          ORDER_STATUS.ACCEPTED,
          orderInfo.serviceType,
          orderInfo.tableName || orderInfo.orderNumber,
          orderInfo.tableId,
          orderInfo.origin,
          orderInfo.desiredTime,
        );
      }

      logger.info('[KDS] Add items to KDS', {
        orderId,
        itemsToAdd: nonServiceChargeItems,
      });

      nonServiceChargeItems.forEach((item) => {
        logger.info('[KDS] Adding item to KDS');
        orderStatusApi.addItem(
          item.itemId,
          item.categoryId,
          orderId!,
          item.message || '',
          item.variations.map((v) => v.id),
          item.removedVariations.map((v) => v.id),
          (item.defaultOrderStatus as ORDER_STATUS) || ORDER_STATUS.ACCEPTED,
          item.id,
          item.parentId,
          getItemCourseId(item) || undefined,
        );
      });
    }
  }

  return {
    orderId,
    orderIdsByCourse,
    itemsToAdd,
    homelessItems,
  };
}
